import {
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  Fade,
  Slide,
} from "@mui/material";
import {
  Visibility,
  Lock,
  VisibilityOff,
} from "@mui/icons-material";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useSnackbar } from "../../providers/SnackbarProvider";
import api from "../../api/client";
import { useLocation, useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import changepswdImg from "../../assets/images/changepassword.png";

const schema = Yup.object().shape({
  currentPassword: Yup.string().required("Current password is required"),
  newPassword: Yup.string()
    .required("New Password is required")
    .min(8, "Password must be at least 8 characters long")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&*(),.?])[A-Za-z\d!@#$%^&*(),.?]{8,}$/,
      "Password must contain at least one uppercase letter, one lowercase letter, one special character (!@#$%^&*(),.?)"
    ),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("newPassword")], "Passwords do not match")
    .required("Confirm password is required"),
});

const ChangePassword = () => {
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mounted, setMounted] = useState(false);
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const storedUserData = JSON.parse(localStorage.getItem("userData") || "{}");
  const userId = storedUserData?.user_id || null;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const payload = {
        user_id: userId,
        old_password: data.currentPassword,
        new_password: data.newPassword,
        otp: Number(data.otp),
      };
      console.log("payload", payload);

      const response = await api.post("ChangePassword", payload);

      if (response?.data?.status === "success") {
        showSnackbar("success", "Password Updated Successfully");
        setTimeout(() => navigate("/"), 1000);
      } else {
        showSnackbar(
          "error",
          response?.data?.message || "Something went wrong"
        );
      }
    } catch (error) {
      console.error("Error during password reset:", error);
      const apiError =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        "Something went wrong. Please try again.";
      showSnackbar("error", apiError);
    } finally {
      setLoading(false);
    }
  };

  const handleBackClick = () => {
    setMounted(false);
    setTimeout(() => navigate(-1), 300);
  };

  return (
    <Fade in={mounted} timeout={500}>
      <Box
        sx={{
          minHeight: "100vh",
          // background: "linear-gradient(135deg, #f5f9ff 0%, #e8f2ff 100%)",
          p: { xs: 1, sm: 2, md: 3 },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          transition: "all 0.3s ease-in-out",
        }}
      >
        <Slide in={mounted} direction="right" timeout={400}>
          <Grid
            item
            sx={{
              display: "flex",
              alignItems: "center",
              color: "#1976d2",
              gap: 1,
              cursor: "pointer",
              width: "100%",
              maxWidth: 800,
              mb: { xs: 2, sm: 3 },
              p: 1,
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "translateX(-5px)",
                color: "#1565c0",
              },
            }}
            onClick={handleBackClick}
          >
            <ArrowBackIcon sx={{ fontSize: "20px" }} />
            <Typography
              sx={{ 
                fontWeight: 600, 
                fontSize: "16px", 
                color: "inherit",
                transition: "color 0.3s ease"
              }}
            >
              Back
            </Typography>
          </Grid>
        </Slide>

        <Slide in={mounted} direction="up" timeout={600}>
          <Box
            sx={{
              width: { xs: "95%", sm: "85%", md: "70%", lg: "60%" },
              maxWidth: 800,
              border: "1px solid #e0e0e0",
              borderRadius: "16px",
              boxShadow: "0 8px 32px rgba(25, 118, 210, 0.15)",
              backgroundColor: "white",
              overflow: "hidden",
              transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
              "&:hover": {
                boxShadow: "0 12px 48px rgba(25, 118, 210, 0.2)",
                transform: "translateY(-2px)",
              },
            }}
          >
            <Box
              sx={{
                borderBottom: "1px solid #f0f0f0",
                p: { xs: 2, sm: 3, md: 4 },
                background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
                color: "white",
              }}
            >
              <Grid container alignItems="center" spacing={2}>
                <Grid item>
                  <Box
                    component="img"
                    src={changepswdImg}
                    alt="Change Password"
                    sx={{ 
                      width: { xs: 55, sm: 55 }, 
                      height: { xs: 55, sm: 55 },
                      my:"10px"
                      // filter: "brightness(0) invert(1)",
                      // transition: "transform 0.3s ease",
                      // "&:hover": {
                      //   transform: "scale(1.1)",
                      // }
                    }}
                  />
                </Grid>
                <Grid item>
                  <Typography 
                    sx={{ 
                      fontSize: { xs: "24px", sm: "28px", md: "30px" }, 
                      fontWeight: "500",
                      transition: "all 0.3s ease"
                    }}
                  >
                    Change Password
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        {/* COLUMN-WISE FULL WIDTH GRID */}
        <Grid container spacing={3} direction="column">
          {/* Current Password */}
          <Grid item xs={12}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
              <Lock sx={{ color: "#1976d2", fontSize: 22 }} />
              <Typography sx={{ fontWeight: 500, fontSize: "16px" }}>
                Current Password
                <span style={{ color: "#f44336" }}>*</span>
              </Typography>
            </Box>

            <TextField
              type={showCurrentPassword ? "text" : "password"}
              fullWidth
              {...register("currentPassword")}
              placeholder="Enter your current password"
              error={!!errors.currentPassword}
              helperText={errors.currentPassword?.message}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        setShowCurrentPassword(!showCurrentPassword)
                      }
                      sx={{
                        color: "#1976d2",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          backgroundColor: "rgba(25, 118, 210, 0.1)",
                          transform: "scale(1.1)",
                        },
                      }}
                    >
                      {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: "12px",
                  height: "50px",
                  "&:focus-within": {
                    boxShadow: "0 0 0 2px rgba(25, 118, 210, 0.2)",
                  },
                },
              }}
            />
          </Grid>

          {/* New Password */}
          <Grid item xs={12}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
              <KeyOutlinedIcon sx={{ color: "#1976d2", fontSize: 22 }} />
              <Typography sx={{ fontWeight: 500, fontSize: "16px" }}>
                New Password
                <span style={{ color: "#f44336" }}>*</span>
              </Typography>
            </Box>

            <TextField
              type={showNewPassword ? "text" : "password"}
              fullWidth
              {...register("newPassword")}
              placeholder="Create a new password"
              error={!!errors.newPassword}
              helperText={errors.newPassword?.message}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      sx={{
                        color: "#1976d2",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          backgroundColor: "rgba(25, 118, 210, 0.1)",
                          transform: "scale(1.1)",
                        },
                      }}
                    >
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: "12px",
                  height: "50px",
                  "&:focus-within": {
                    boxShadow: "0 0 0 2px rgba(25, 118, 210, 0.2)",
                  },
                },
              }}
            />
          </Grid>

          {/* Confirm Password */}
          <Grid item xs={12}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
              <KeyOutlinedIcon sx={{ color: "#1976d2", fontSize: 22 }} />
              <Typography sx={{ fontWeight: 500, fontSize: "16px" }}>
                Confirm Password
                <span style={{ color: "#f44336" }}>*</span>
              </Typography>
            </Box>

            <TextField
              type={showConfirmPassword ? "text" : "password"}
              fullWidth
              {...register("confirmPassword")}
              placeholder="Confirm your new password"
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword?.message}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      sx={{
                        color: "#1976d2",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          backgroundColor: "rgba(25, 118, 210, 0.1)",
                          transform: "scale(1.1)",
                        },
                      }}
                    >
                      {showConfirmPassword ? (
                        <VisibilityOff />
                      ) : (
                        <Visibility />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: "12px",
                  height: "50px",
                  "&:focus-within": {
                    boxShadow: "0 0 0 2px rgba(25, 118, 210, 0.2)",
                  },
                },
              }}
            />
          </Grid>
        </Grid>

        {/* Buttons */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            justifyContent: "flex-end",
            mt: 4,
            flexWrap: { xs: "wrap", sm: "nowrap" },
          }}
        >
          {/* CLEAR BUTTON */}
          <Button
            type="button"
            variant="outlined"
            onClick={() => reset()}
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              px: 4,
              py: 1,
              borderColor: "#1976d2",
              color: "#1976d2",
              fontWeight: 500,
              fontSize: "16px",
              "&:hover": {
                backgroundColor: "rgba(25, 118, 210, 0.1)",
                borderColor: "#1565c0",
              },
            }}
          >
            Clear
          </Button>

          {/* SUBMIT BUTTON */}
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              px: 4,
              py: 1,
              background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
              fontWeight: 500,
              fontSize: "16px",
              "&:hover": {
                backgroundColor: "#1565c0",
                transform: "translateY(-1px)",
                boxShadow: "0 6px 20px rgba(25, 118, 210, 0.4)",
              },
              "&:disabled": {
                background: "#90caf9",
              },
            }}
          >
            {loading ? "Updating..." : "Update Password"}
          </Button>
        </Box>
      </form>
    </Box>
          </Box>
        </Slide>

        {/* Add CSS for spinner animation */}
        <style jsx>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </Box>
    </Fade>
  );
};

export default ChangePassword;